import { getRecipientKeys } from '../crypto/recipientKeyCache';
import { unwrapContentKeyForRecipient } from '../crypto/recipientKeyUnwrap';
import { decryptMediaBase64WithContentKey } from './mediaCrypto';

type AnyRecord = Record<string, any>;

type ThumbnailFailureEntry = {
  expiresAt: number;
  reason: string;
};

const WEB_THUMB_SUCCESS_CACHE_MAX = 180;
const WEB_THUMB_FAILURE_CACHE_MAX = 500;

const WEB_THUMB_TRANSIENT_FAILURE_TTL_MS = 60_000;
const WEB_THUMB_CRYPTO_FAILURE_TTL_MS = 30 * 60_000;

const webThumbnailSuccessCache = new Map<string, string>();
const webThumbnailFailureCache = new Map<string, ThumbnailFailureEntry>();
const webThumbnailInFlight = new Map<string, Promise<string | null>>();
const webThumbnailLoggedFailures = new Set<string>();

function boundedMapSet<K, V>(
  map: Map<K, V>,
  key: K,
  value: V,
  maxEntries: number
) {
  if (map.has(key)) {
    map.delete(key);
  }

  map.set(key, value);

  while (map.size > maxEntries) {
    const oldestKey = map.keys().next().value as K | undefined;
    if (oldestKey === undefined) break;
    map.delete(oldestKey);
  }
}

function webThumbnailDebugEnabled() {
  try {
    return (
      window.localStorage.getItem('intimsync.messages.debugTools') === '1'
    );
  } catch {
    return false;
  }
}

function stableThumbnailItemId(item: any): string {
  return String(
    item?.mediaId ||
    item?.media_id ||
    item?.publicId ||
    item?.public_id ||
    item?.id ||
    item?.clientMessageId ||
    item?.client_message_id ||
    ''
  ).trim();
}

function thumbnailCacheKey(args: {
  item: any;
  currentUserId: string;
  thumb: AnyRecord;
  ciphertextBase64: string;
  ivBase64: string;
  candidates: AnyRecord[];
}) {
  const itemId = stableThumbnailItemId(args.item);

  const candidateFingerprint = args.candidates
    .map((env) => {
      const recipientUserId = String(
        env?.recipientUserId ||
        env?.recipient_user_id ||
        ''
      ).trim();

      const senderKeyId = String(
        env?.senderKeyId ||
        env?.sender_key_id ||
        ''
      ).trim();

      const wrappedKeyBase64 = String(
        env?.wrappedKeyBase64 ||
        env?.wrapped_key_base64 ||
        ''
      ).trim();

      return [
        recipientUserId,
        senderKeyId,
        wrappedKeyBase64.length,
        wrappedKeyBase64.slice(0, 24),
        wrappedKeyBase64.slice(-24),
      ].join(':');
    })
    .join('|');

  return [
    itemId || 'anonymous-media',
    args.currentUserId,
    getThumbMimeType(args.thumb),
    args.ciphertextBase64.length,
    args.ciphertextBase64.slice(0, 24),
    args.ciphertextBase64.slice(-24),
    args.ivBase64,
    candidateFingerprint,
  ].join('::');
}

function classifyThumbnailFailure(tried: any[]) {
  const reasons = tried
    .map((entry) => String(entry?.error || '').toLowerCase())
    .filter(Boolean);

  if (
    reasons.some((reason) =>
      reason.includes('missing_public_key') ||
      reason.includes('missing_sender_or_wrapped_key')
    )
  ) {
    return {
      reason: reasons.join(',') || 'missing_key_material',
      ttlMs: WEB_THUMB_TRANSIENT_FAILURE_TTL_MS,
    };
  }

  return {
    reason: reasons.join(',') || 'thumbnail_decrypt_failed',
    ttlMs: WEB_THUMB_CRYPTO_FAILURE_TTL_MS,
  };
}

function dataUrlFromBase64(mimeType: string, base64: string) {
  if (!base64) return '';
  if (base64.startsWith('data:')) return base64;
  return `data:${mimeType || 'image/jpeg'};base64,${base64}`;
}

function parseManifest(value: any): AnyRecord {
  if (!value) return {};
  if (typeof value === 'object') return value;
  if (typeof value === 'string') {
    try {
      return JSON.parse(value);
    } catch {
      return {};
    }
  }
  return {};
}

function getManifestFromItem(item: any): AnyRecord {
  return parseManifest(
    item?.manifestJson ||
    item?.manifest_json ||
    item?.encryptedManifest ||
    item?.encrypted_manifest_json ||
    item?.manifest ||
    item?.payload?.manifestJson ||
    item?.payload?.manifest_json ||
    {}
  );
}

function getThumbnailFromManifest(manifest: AnyRecord): AnyRecord | null {
  const thumb =
    manifest?.thumbnail ||
    manifest?.encryptedThumbnail ||
    manifest?.encrypted_thumbnail ||
    manifest?.previewThumbnail ||
    manifest?.preview_thumbnail ||
    null;

  return thumb && typeof thumb === 'object' ? thumb : null;
}

function getThumbMimeType(thumb: AnyRecord) {
  return String(
    thumb?.mimeType ||
    thumb?.mime_type ||
    thumb?.contentType ||
    thumb?.content_type ||
    'image/jpeg'
  );
}

function directThumbnailDataUrl(thumb: AnyRecord): string {
  const mimeType = getThumbMimeType(thumb);

  const direct =
    thumb?.dataUrl ||
    thumb?.dataURL ||
    thumb?.previewDataUrl ||
    thumb?.preview_data_url ||
    thumb?.thumbnailDataUrl ||
    thumb?.thumbnail_data_url ||
    '';

  if (direct) return String(direct);

  const plainBase64 =
    thumb?.plaintextBase64 ||
    thumb?.plainBase64 ||
    thumb?.base64 ||
    thumb?.thumbnailBase64 ||
    thumb?.thumbnail_base64 ||
    '';

  if (plainBase64) return dataUrlFromBase64(mimeType, String(plainBase64));

  return '';
}

function encryptedThumbCiphertextBase64(thumb: AnyRecord): string {
  return String(
    thumb?.ciphertextBase64 ||
    thumb?.ciphertext_base64 ||
    thumb?.ciphertext ||
    thumb?.encryptedBase64 ||
    thumb?.encrypted_base64 ||
    thumb?.data ||
    ''
  ).replace(/^data:[^,]+,/, '').trim();
}

function encryptedThumbIvBase64(thumb: AnyRecord): string {
  const enc = thumb?.encryption || {};
  return String(
    thumb?.ivBase64 ||
    thumb?.iv_base64 ||
    thumb?.iv ||
    enc?.ivBase64 ||
    enc?.iv_base64 ||
    enc?.iv ||
    enc?.nonceBase64 ||
    enc?.nonce_base64 ||
    enc?.nonce ||
    ''
  ).trim();
}

function allCandidateThumbnailEnvelopes(thumb: AnyRecord, currentUserId: string): AnyRecord[] {
  const out: AnyRecord[] = [];
  const seen = new Set<string>();
  const encryption = thumb?.encryption || {};

  function add(env: any, source: string) {
    if (!env) return;

    const wrappedKeyBase64 = String(
      env?.wrappedKeyBase64 ||
      env?.wrapped_key_base64 ||
      env?.wrappedContentKeyBase64 ||
      env?.wrapped_content_key_base64 ||
      ''
    ).trim();

    const senderKeyId = String(
      env?.senderKeyId ||
      env?.sender_key_id ||
      env?.keyId ||
      env?.key_id ||
      ''
    ).trim();

    if (!wrappedKeyBase64 || !senderKeyId) return;

    const recipientUserId = String(
      env?.recipientUserId ||
      env?.recipient_user_id ||
      currentUserId ||
      ''
    ).trim();

    const key = `${source}:${recipientUserId}:${senderKeyId}:${wrappedKeyBase64}`;
    if (seen.has(key)) return;
    seen.add(key);

    out.push({
      ...env,
      recipientUserId,
      senderKeyId,
      wrappedKeyBase64,
      __source: source,
    });
  }

  const envelopes = Array.isArray(encryption?.recipientEnvelopes)
    ? encryption.recipientEnvelopes
    : Array.isArray(encryption?.recipient_envelopes)
      ? encryption.recipient_envelopes
      : Array.isArray(thumb?.recipientEnvelopes)
        ? thumb.recipientEnvelopes
        : [];

  for (const env of envelopes) {
    if (String(env?.recipientUserId || env?.recipient_user_id || '') === currentUserId) {
      add(env, 'current-user');
    }
  }

  for (const env of envelopes) {
    add(env, 'fallback-any-recipient');
  }

  add(encryption?.wrappedContentKey || encryption?.wrapped_content_key, 'legacy-wrappedContentKey');

  return out;
}

function looksLikeImageBase64(base64: string, mimeType: string) {
  const s = String(base64 || '').slice(0, 24);
  const mime = String(mimeType || '').toLowerCase();

  if (mime.includes('png')) return s.startsWith('iVBORw0KGgo');
  if (mime.includes('webp')) return s.startsWith('UklGR');
  if (mime.includes('gif')) return s.startsWith('R0lGOD');
  if (mime.includes('jpeg') || mime.includes('jpg')) return s.startsWith('/9j/');

  return (
    s.startsWith('/9j/') ||
    s.startsWith('iVBORw0KGgo') ||
    s.startsWith('UklGR') ||
    s.startsWith('R0lGOD')
  );
}

export function inspectWebManifestThumbnail(item: any, currentUserId?: string | number | null) {
  const manifest = getManifestFromItem(item);
  const thumb = getThumbnailFromManifest(manifest);

  if (!thumb) {
    return {
      ok: false,
      reason: 'no_thumbnail',
      manifestKeys: manifest ? Object.keys(manifest) : [],
    };
  }

  const current = String(currentUserId || '').trim();
  const envelopes = current ? allCandidateThumbnailEnvelopes(thumb, current) : [];

  return {
    ok: true,
    reason: 'has_thumbnail',
    manifestKeys: manifest ? Object.keys(manifest) : [],
    thumbKeys: Object.keys(thumb),
    encryptionKeys: thumb?.encryption ? Object.keys(thumb.encryption) : [],
    hasDirect: !!directThumbnailDataUrl(thumb),
    hasCiphertext: !!encryptedThumbCiphertextBase64(thumb),
    hasIv: !!encryptedThumbIvBase64(thumb),
    envelopeCount: envelopes.length,
    envelopeKeys: envelopes[0] ? Object.keys(envelopes[0]) : [],
    mimeType: getThumbMimeType(thumb),
  };
}

export async function decryptWebManifestThumbnailToDataUrl(args: {
  item: any;
  currentUserId: string | number | null | undefined;
}): Promise<string | null> {
  const currentUserId = String(args.currentUserId || '').trim();
  if (!currentUserId) return null;

  const manifest = getManifestFromItem(args.item);
  const thumb = getThumbnailFromManifest(manifest);
  if (!thumb) return null;

  const direct = directThumbnailDataUrl(thumb);
  if (direct) return direct;

  const ciphertextBase64 = encryptedThumbCiphertextBase64(thumb);
  const ivBase64 = encryptedThumbIvBase64(thumb);
  const mimeType = getThumbMimeType(thumb);

  if (!ciphertextBase64 || !ivBase64) return null;

  const candidates = allCandidateThumbnailEnvelopes(
    thumb,
    currentUserId
  );

  if (!candidates.length) return null;

  const cacheKey = thumbnailCacheKey({
    item: args.item,
    currentUserId,
    thumb,
    ciphertextBase64,
    ivBase64,
    candidates,
  });

  const cachedSuccess =
    webThumbnailSuccessCache.get(cacheKey);

  if (cachedSuccess) {
    return cachedSuccess;
  }

  const cachedFailure =
    webThumbnailFailureCache.get(cacheKey);

  if (cachedFailure) {
    if (cachedFailure.expiresAt > Date.now()) {
      return null;
    }

    webThumbnailFailureCache.delete(cacheKey);
    webThumbnailLoggedFailures.delete(cacheKey);
  }

  const existingPromise =
    webThumbnailInFlight.get(cacheKey);

  if (existingPromise) {
    return existingPromise;
  }

  const decryptPromise = (async (): Promise<string | null> => {
    const tried: any[] = [];

    for (const env of candidates) {
      const senderKeyId = String(
        env?.senderKeyId || ''
      ).trim();

      const wrappedKeyBase64 = String(
        env?.wrappedKeyBase64 || ''
      ).trim();

      const recipientUserId = String(
        env?.recipientUserId ||
        currentUserId ||
        ''
      ).trim();

      if (
        !senderKeyId ||
        !wrappedKeyBase64 ||
        !recipientUserId
      ) {
        tried.push({
          source: env?.__source,
          error: 'missing_sender_or_wrapped_key',
        });
        continue;
      }

      try {
        const recipientKeys =
          await getRecipientKeys([recipientUserId]);

        const key = recipientKeys?.[0];

        if (!key?.publicKeyBase64) {
          tried.push({
            source: env?.__source,
            recipientUserId,
            error: 'missing_public_key',
          });
          continue;
        }

        const contentKeyBase64 =
          unwrapContentKeyForRecipient({
            recipientPublicKeyBase64:
              key.publicKeyBase64,
            senderKeyId,
            wrappedKeyBase64,
          });

        const plainBase64 =
          decryptMediaBase64WithContentKey({
            ciphertextBase64,
            ivBase64,
            contentKeyBase64,
          });

        if (
          !looksLikeImageBase64(
            plainBase64,
            mimeType
          )
        ) {
          tried.push({
            source: env?.__source,
            recipientUserId,
            error: 'bad_thumbnail_magic',
          });
          continue;
        }

        const result = dataUrlFromBase64(
          mimeType,
          plainBase64
        );

        boundedMapSet(
          webThumbnailSuccessCache,
          cacheKey,
          result,
          WEB_THUMB_SUCCESS_CACHE_MAX
        );

        webThumbnailFailureCache.delete(cacheKey);
        webThumbnailLoggedFailures.delete(cacheKey);

        return result;
      } catch (e: any) {
        tried.push({
          source: env?.__source,
          recipientUserId,
          error: e?.message || String(e),
        });
      }
    }

    const failure = classifyThumbnailFailure(tried);

    boundedMapSet(
      webThumbnailFailureCache,
      cacheKey,
      {
        expiresAt: Date.now() + failure.ttlMs,
        reason: failure.reason,
      },
      WEB_THUMB_FAILURE_CACHE_MAX
    );

    if (
      webThumbnailDebugEnabled() &&
      !webThumbnailLoggedFailures.has(cacheKey)
    ) {
      webThumbnailLoggedFailures.add(cacheKey);

      console.debug(
        '[Messages web] manifest thumbnail decrypt candidates failed',
        {
          mediaId: stableThumbnailItemId(args.item),
          currentUserId,
          retryAfterMs: failure.ttlMs,
          tried,
        }
      );
    }

    return null;
  })();

  webThumbnailInFlight.set(
    cacheKey,
    decryptPromise
  );

  try {
    return await decryptPromise;
  } finally {
    if (
      webThumbnailInFlight.get(cacheKey) ===
      decryptPromise
    ) {
      webThumbnailInFlight.delete(cacheKey);
    }
  }
}

export function hasWebManifestThumbnail(item: any): boolean {
  const manifest = getManifestFromItem(item);
  return !!getThumbnailFromManifest(manifest);
}
